import { api } from "../services/api";
import { PRAXIS_PLAY_EVENTS } from "../integrations/posthog/events";

/**
 * Shared disconnect logic used by the PraxisCall context.
 * Keeps endReason handling and error cleanup in one place to avoid drift.
 */

export interface DisconnectSetters {
  setLoading: (v: boolean) => void;
  setCallDetails: (v: object) => void;
  setCallStatus: (v: string) => void;
  setDataSavingProgress: (v: number) => void;
}

export interface TranscriptRef {
  current: { role: string; content: string }[];
}

/**
 * Execute the endReason flow: end session via API, fire analytics, reset state.
 * No report is generated when endReason is provided.
 */
export async function executeEndReasonDisconnect(
  callId: string,
  transcripts: { role: string; content: string }[],
  endReason: string,
  captureEvent: (event: string, props?: object) => void,
  setters: DisconnectSetters,
  transcriptRef: TranscriptRef
): Promise<void> {
  await api.endCall(callId, { messages: transcripts, endReason });
  captureEvent(PRAXIS_PLAY_EVENTS.PRAXISPLAY_SESSION_TERMINATED, {
    session_id: callId,
    termination_source: "user_action",
    messages_exchanged: transcripts?.length || 0,
  });
  transcriptRef.current = [];
  setters.setCallDetails({});
  setters.setLoading(false);
  setters.setCallStatus("idle");
  setters.setDataSavingProgress(0);
}

/**
 * Reset call UI state after an error. Intended for endReason flow errors only:
 * when executeEndReasonDisconnect (endCall API) fails, we clear state so the
 * user is not stuck. For normal disconnect flow errors (createPlayback,
 * fetchReports), callDetails should be preserved for UI; only setLoading(false)
 * is applied.
 */
export function clearCallStateOnDisconnectError(
  setters: DisconnectSetters,
  transcriptRef: TranscriptRef
): void {
  setters.setLoading(false);
  transcriptRef.current = [];
  setters.setCallDetails({});
  setters.setCallStatus("idle");
  setters.setDataSavingProgress(0);
}
